import type { CSSProperties } from "react";

export type StatusTone = "success" | "warning" | "danger" | "info" | "neutral";

interface StatusPillProps {
  label: string;
  tone?: StatusTone;
  /** When true, render as `<dot> <label>` with no background fill — for
   *  in-table status columns where dense rows would crowd otherwise. */
  inline?: boolean;
  style?: CSSProperties;
}

/**
 * Drop-in replacement for the AntD `<Tag color="...">` pattern that gave
 * the legacy UI its "Bootstrap 4 admin template" feel. Maps the few tones
 * we actually use to DIT-approved score-band colors.
 */
export function StatusPill({ label, tone = "neutral", inline = false, style }: StatusPillProps) {
  if (inline) {
    const dotClass =
      tone === "success"
        ? "apex-dot--green"
        : tone === "warning"
          ? "apex-dot--yellow"
          : tone === "danger"
            ? "apex-dot--red"
            : tone === "info"
              ? "apex-dot--blue"
              : // "neutral" inline dots use the DIT primary navy so the
                // baseline state (Submitted, etc.) reads as the brand spine
                // instead of a muddy slate.
                "apex-dot--primary";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13,
          color: "var(--apex-ink-body)",
          ...style,
        }}
      >
        <span className={`apex-dot ${dotClass}`} aria-hidden />
        {label}
      </span>
    );
  }
  return (
    <span className={`apex-status-cell apex-status-cell--${tone}`} style={style}>
      {label}
    </span>
  );
}
